import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut } from 'lucide-react';
import { MobileHeaderProvider, ResizedImage } from '@ah/design/mobile';
import { useMe } from './useAuth';
import { apiLogoutBackground } from './api';
import { clearSession } from './accounts';

function AccountBlock() {
  const { data: me } = useMe();
  const [confirmOpen, setConfirmOpen] = useState(false);

  function confirmLogout() {
    apiLogoutBackground();
    clearSession();
    window.location.href = '/';
  }

  if (!me) return null;
  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-accent">
        <Link to="/profile" className="flex items-center gap-3 flex-1 min-w-0">
          {me.pictureUrl ? (
            <ResizedImage src={me.pictureUrl} renderWidth={40} fallback={false} alt="" className="w-10 h-10 rounded-full object-cover bg-muted" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-muted" />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm truncate">{me.name}</div>
            {me.email && (
              <div className="text-xs text-muted-foreground truncate">{me.email}</div>
            )}
          </div>
        </Link>
        <button
          type="button"
          aria-label="Log out"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground active:bg-accent focus:outline-none"
        >
          <LogOut size={20} />
        </button>
      </div>
      <AnimatePresence>
        {confirmOpen && (
          <>
            <motion.div
              key="logout-backdrop"
              className="fixed inset-0 z-[60] bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(false);
              }}
            />
            <motion.div
              key="logout-dialog"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              className="fixed inset-0 z-[60] flex items-center justify-center p-6 pointer-events-none"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
            >
              <div className="pointer-events-auto w-full max-w-sm rounded-2xl bg-background shadow-xl border border-border p-5">
                <h2 className="text-base font-semibold">Log out?</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  You'll need to sign in again to access your profile.
                </p>
                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmOpen(false)}
                    className="flex-1 h-10 rounded-full bg-accent text-foreground font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmLogout}
                    className="flex-1 h-10 rounded-full bg-destructive text-destructive-foreground font-medium text-sm"
                  >
                    Log out
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export function AppChromeProvider({ children }: { children: React.ReactNode }) {
  return (
    <MobileHeaderProvider
      drawerAccountBlock={<AccountBlock />}
      defaultTitle={null}
    >
      {children}
    </MobileHeaderProvider>
  );
}
